/**
 * Pure mapping from a placed tree instance (the deterministic output of
 * vegetationPlacement.ts / issue #56) to a baked (species, structural variant)
 * pool, plus the per-instance jitter that keeps neighbours from looking
 * identical (issue #58 AC: "no two adjacent trees look identical"). Three.js
 * free and node-testable; the three.js wrapper (treePools.ts) consumes it.
 *
 * The placement layer already assigns each tree a `variant` in 0..2 meaning a
 * species-ish look (0 broadleaf / 1 conifer / 2 small tree — see AREA_PLANTING
 * in vegetationPlacement.ts). We reuse that as the SPECIES selector and derive a
 * STRUCTURAL variant + scale/tint jitter from the instance's world position, so
 * the mapping is deterministic (same tree every load) yet varied between
 * neighbours. Yaw jitter already comes from the placement `rotationY`.
 */

/** Deterministic fractional hash in [0,1) from two coords (hash01 idiom). */
export function hashXZ(x: number, z: number, salt: number): number {
  const v = Math.sin((x * 12.9898 + z * 78.233 + salt * 37.719) * 1.0) * 43758.5453;
  return v - Math.floor(v);
}

export interface PoolSelector {
  speciesIndex: number;
  variantIndex: number;
}

/** Maps a placement `variant` (0..speciesCount-1, clamped) to a species, and
 * hashes the world position to pick one of `structuralVariants` skeletons. */
export function poolFor(
  placementVariant: number,
  x: number,
  z: number,
  speciesCount: number,
  structuralVariants: number,
): PoolSelector {
  const speciesIndex = Math.max(0, Math.min(speciesCount - 1, Math.floor(placementVariant)));
  const variantIndex = Math.floor(hashXZ(x, z, 1.3) * structuralVariants) % structuralVariants;
  return { speciesIndex, variantIndex };
}

/** Per-instance uniform scale multiplier in [1-amp, 1+amp], deterministic. */
export function scaleJitter(x: number, z: number, amp = 0.14): number {
  return 1 + (hashXZ(x, z, 5.7) - 0.5) * 2 * amp;
}

/** Per-instance foliage tint (rgb, ~0.8..1.1 around white) so crowns vary in
 * hue/value between neighbours without a per-instance material. Biased green:
 * the red channel is pulled down a touch more than green/blue. */
export function tintJitter(x: number, z: number): [number, number, number] {
  const h = hashXZ(x, z, 9.1);
  const warm = 0.85 + h * 0.25; // 0.85..1.10
  const g = 0.88 + hashXZ(x, z, 2.2) * 0.2; // 0.88..1.08
  return [warm * 0.92, g, warm * 0.82];
}
